'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { useState } from 'react'
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs'

import { format } from 'date-fns'

import { CornerBrackets } from '../../components/kinetic/CornerBrackets'

const POSTS_PER_PAGE = 9

export default function PostsClientPage({
  posts,
}: {
  posts: Array<{
    slug: string
    title: string
    date: string
    heroImg?: string
    excerpt?: string
    author?: { name?: string; avatar?: string }
  }>
}) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState<
    'all' | 'data-engineering' | 'aws' | 'snowflake'
  >('all')

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, activeCategory])

  const estimateReadMinutes = (excerpt?: string) => {
    const text = (excerpt || '').trim()
    if (!text) return 8
    const words = text.split(/\s+/g).filter(Boolean).length
    // ~220 wpm for technical reading; clamp to a sensible range.
    return Math.max(4, Math.min(18, Math.round(words / 220)))
  }

  const sortedPosts = React.useMemo(() => {
    return [...posts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [posts])

  // Client-side filtering (search + category)
  const filteredPosts = React.useMemo(() => {
    let result = sortedPosts

    // Search
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim()
      result = result.filter(
        p => p.title.toLowerCase().includes(q) || (p.excerpt || '').toLowerCase().includes(q)
      )
    }

    // Category (simple keyword heuristic for current content)
    if (activeCategory !== 'all') {
      result = result.filter(p => {
        const text = (p.title + ' ' + (p.excerpt || '')).toLowerCase()
        if (activeCategory === 'snowflake') return text.includes('snowflake')
        if (activeCategory === 'aws') return text.includes('aws') || text.includes('amazon')
        if (activeCategory === 'data-engineering') {
          return (
            text.includes('data') ||
            text.includes('engineering') ||
            text.includes('pipeline') ||
            text.includes('platform') ||
            text.includes('governance')
          )
        }
        return true
      })
    }

    return result
  }, [sortedPosts, searchTerm, activeCategory])

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE)
  const currentPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  )

  if (posts.length === 0) {
    return <p className="text-kinetic-on-surface-variant">No articles yet. Check back soon.</p>
  }

  if (filteredPosts.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="mb-2 text-kinetic-on-surface-variant">No posts match your filters.</p>
        <button
          onClick={() => {
            setSearchTerm('')
            setActiveCategory('all')
          }}
          className="font-mono text-sm uppercase tracking-widest text-kinetic-primary-container hover:underline"
        >
          Clear filters
        </button>
      </div>
    )
  }

  const categories: { label: string; value: typeof activeCategory }[] = [
    { label: 'All', value: 'all' },
    { label: 'Data Engineering', value: 'data-engineering' },
    { label: 'AWS', value: 'aws' },
    { label: 'Snowflake', value: 'snowflake' },
  ]

  return (
    <div className="flex flex-col">
      {/* Functional filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Search articles..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="border-kinetic-outline-variant/30 focus:border-kinetic-primary-container/60 min-w-[220px] rounded-none border bg-kinetic-surface-container-low px-4 py-2.5 text-sm text-kinetic-on-surface placeholder:text-kinetic-outline focus:outline-none"
        />
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setActiveCategory(cat.value)}
              className={`px-4 py-2 font-headline text-sm font-semibold uppercase tracking-tight transition-all ${
                activeCategory === cat.value
                  ? 'bg-kinetic-primary-container text-kinetic-on-primary'
                  : 'border-kinetic-outline-variant/30 hover:border-kinetic-primary-container/40 border bg-kinetic-surface-container-low text-kinetic-on-surface-variant'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
          Sort: Newest
        </span>
        {filteredPosts.length !== sortedPosts.length && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-kinetic-primary-container">
            {filteredPosts.length} of {sortedPosts.length}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {currentPosts.map(post => {
          const formattedDate = format(new Date(post.date), 'MMM d, yyyy')
          const sortedIndex = sortedPosts.findIndex(p => p.slug === post.slug)
          const nodeIndex = String(sortedIndex + 1).padStart(2, '0')
          const readMins = estimateReadMinutes(post.excerpt)

          return (
            <Link
              key={post.slug}
              href={`/post/${post.slug}`}
              className="border-kinetic-outline-variant/30 hover:border-kinetic-primary-container/40 group relative block border bg-kinetic-surface-container-low p-5 transition-all"
            >
              <CornerBrackets className="pointer-events-none opacity-0 transition-opacity group-hover:opacity-100" />

              <div className="bg-kinetic-surface-container-highest/50 mb-4 h-32 w-full overflow-hidden">
                {post.heroImg && (
                  <Image
                    src={post.heroImg}
                    alt={post.title}
                    width={400}
                    height={128}
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.04]"
                  />
                )}
              </div>
              <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-kinetic-primary-container">
                {`NODE_${nodeIndex}`}
              </p>
              <h3 className="mb-2 line-clamp-2 font-headline text-base font-semibold uppercase tracking-tight text-white transition-colors group-hover:text-kinetic-primary-container">
                {post.title}
              </h3>
              <p className="mb-3 text-sm text-kinetic-on-surface-variant">
                {formattedDate} · {readMins.toString().padStart(2, '0')}m read
              </p>

              <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-kinetic-primary-container group-hover:underline">
                Read_Forward →
              </div>
            </Link>
          )
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-12 flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 text-kinetic-on-surface-variant hover:bg-kinetic-surface-container-low disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="mr-2 hidden font-mono text-[10px] uppercase tracking-widest sm:inline">
              Prev_Batch
            </span>
            <BsChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`h-10 w-10 font-headline text-sm font-medium transition-colors ${
                currentPage === i + 1
                  ? 'bg-kinetic-primary-container text-kinetic-on-primary'
                  : 'text-kinetic-on-surface-variant hover:bg-kinetic-surface-container-low'
              }`}
            >
              {String(i + 1).padStart(2, '0')}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 text-kinetic-on-surface-variant hover:bg-kinetic-surface-container-low disabled:cursor-not-allowed disabled:opacity-50"
          >
            <BsChevronRight className="h-5 w-5" aria-hidden />
            <span className="ml-2 hidden font-mono text-[10px] uppercase tracking-widest sm:inline">
              Next_Batch
            </span>
          </button>
        </div>
      )}
    </div>
  )
}
